/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

// Component imports
import { Button, Col, Empty, Form, Modal, Radio, Row, Select, Skeleton, Space, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';

// Misc imports
import { useEffect } from 'react';
import { useLazyFetchKnowledgeBasesQuery, useUpdateKnowledgeBaseMutation } from '../../store/knowledgeBases/knowledgeBasesApi';
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import { NAV_ORGANIZATION_KNOWLEDGE_BASE_NEW } from '../../constants/navigation';

// Constant declarations
const { Paragraph, Text } = Typography;

const generateInitialValues = (selectedKnowledgeBase, tenantId) => {
  return {
    knowledgeBaseId: selectedKnowledgeBase?.id,
    useGeneratedResponse: selectedKnowledgeBase ? selectedKnowledgeBase?.tenantAssociations?.find(association => association.tenantId === tenantId)?.useGenerative : true
  };
};

const ConnectKnowledgeBaseModal = ({
  isModalOpen = false,
  closeModal = () => {},
  selectedKnowledgeBase
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { orgId, tenantId } = useParams();

  // Redux state
  const [lazyFetchKnowledgeBases, { data: orgKnowledgeBases, isFetching: isFetchingOrgKnowledgeBases }] = useLazyFetchKnowledgeBasesQuery();
  const [updateKnowledgeBase, { isLoading: isUpdatingKnowledgeBase }] = useUpdateKnowledgeBaseMutation();

  const filteredKnowledgeBases = (orgKnowledgeBases?.items || []).filter(kb => !(kb.tenantAssociations || []).map(association => association.tenantId).includes(tenantId));

  // On mount
  useEffect(() => {
    form.resetFields();
  }, []);

  useEffect(() => {
    if(isModalOpen){
      lazyFetchKnowledgeBases({ orgId: orgId, urlParams: { includeTenants: true, pageSize: 500 } }).unwrap();
    }
  }, [isModalOpen]);

  // When the chosen Knowledge Base changes
  useEffect(() => {
    form.setFieldsValue(generateInitialValues(selectedKnowledgeBase, tenantId));
  }, [selectedKnowledgeBase?.id]);

  const onSubmit = async (values) => {
    const knowledgeBaseId = values.knowledgeBaseId || selectedKnowledgeBase?.id;
    const currentTenantAssociation = filteredKnowledgeBases.find(kb=> kb.id === knowledgeBaseId)?.tenantAssociations ?? [];
    const dataToSubmit = {
      tenantAssociationRefs: [
        ...currentTenantAssociation,
        {
          tenantId: tenantId,
          useGenerative: values.useGeneratedResponse
        }]
    };

    const isConnectSuccessful = await updateKnowledgeBase({ orgId: orgId, id: knowledgeBaseId, data: dataToSubmit, tenantId: tenantId });
    if (isConnectSuccessful) {
      closeModal();
      form.resetFields();
    }
  };

  return (
    <Modal
      title={
        <Fragment>
          <Paragraph className="d-flex mb-0">
            {selectedKnowledgeBase ? t('Edit Knowledge Base Connection') : t('Connect New Knowledge Base')}
          </Paragraph>
          <Paragraph className="fs-lg fw-normal text-muted mb-0 mt-2">
            {selectedKnowledgeBase ? t('You can change your preference in each Automated Answer separately.') : t('Choose from the available organization\'s Knowledge Bases')}
          </Paragraph>
        </Fragment>
      }
      open={isModalOpen}
      onCancel={closeModal}
      onOk={onSubmit}
      centered
      footer={
        <Row justify="end">
          {
            !selectedKnowledgeBase ? (
              <Col>
                <Button
                  type="link"
                  size="large"
                  onClickCapture={() => window.open(`${replaceUrlParams(NAV_ORGANIZATION_KNOWLEDGE_BASE_NEW, { orgId })}`)}
                >
                  {t('Add New Knowledge Base')}
                </Button>
              </Col>
            ) : null
          }

          <Col>
            <Space>
              <Button
                size="large"
                loading={isFetchingOrgKnowledgeBases || isUpdatingKnowledgeBase}
                onClick={closeModal}
              >
                {t('Cancel')}
              </Button>
              <Button
                disabled={!(selectedKnowledgeBase || filteredKnowledgeBases.length > 0)}
                type="primary"
                size="large"
                loading={isFetchingOrgKnowledgeBases || isUpdatingKnowledgeBase}
                onClick={() => form.submit()}
              >
                {selectedKnowledgeBase ? t('Save Changes') : t('Connect KB')}
              </Button>
            </Space>

          </Col>
        </Row>
      }
    >
      <Form
        name="connectKnowledgeBaseForm"
        layout="vertical"
        onFinish={onSubmit}
        form={form}
        requiredMark={false}
        initialValues={generateInitialValues(selectedKnowledgeBase, tenantId)}
      >
        {
          filteredKnowledgeBases?.length > 0 ? (
            <Form.Item
              name="knowledgeBaseId"
              label={t('Choose one of the available Knowledge Bases')}
              className="mb-8"
              hidden={selectedKnowledgeBase}
              rules={[{
                required: true,
                message: `${t('Please choose a knowledge base.')}`
              }]}
            >
              <Select
                size="large"
                placeholder={t('Select a knowledge base')}
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                getPopupContainer={triggerNode => triggerNode.parentElement}
              >
                {
                  filteredKnowledgeBases.map(kb => (
                    <Select.Option key={kb.id} value={kb.id}>{kb.name}</Select.Option>
                  ))
                }
              </Select>
            </Form.Item>
          ) : filteredKnowledgeBases?.length === 0 && !selectedKnowledgeBase ? (
            <Empty
              description={<Text type="secondary">{t('There are no knowledge bases available for this organization')}.</Text>}
            />
          ) : isFetchingOrgKnowledgeBases ? (
            <Skeleton active />
          ) : null
        }

        { selectedKnowledgeBase || filteredKnowledgeBases?.length > 0
          ? <Form.Item
            name="useGeneratedResponse"
            label={t('By default the Agent will reply with:')}
            rules={[{
              required: true,
              message: `${t('Please choose an option.')}`
            }]}
            tooltip={{
              icon: <QuestionCircleOutlined />,
              title: (
                <Fragment>
                  <Paragraph>{t('The Agent will reply with AI generated text or with the whole Article body by default.')}</Paragraph>
                  <Text>{t('You can change your preference in each Automated Answer separately.')}</Text>
                </Fragment>
              )
            }}
          >
            <Radio.Group>
              <Space direction="vertical">
                <Radio value={true}>{t('AI Generated Text')}</Radio>
                <Radio value={false}>{t('Article Body')}</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
          : null
        }
      </Form>
    </Modal>
  );
};

ConnectKnowledgeBaseModal.propTypes = {
  isModalOpen: PropTypes.bool,
  closeModal: PropTypes.func,
  selectedKnowledgeBase: PropTypes.string
};

export default ConnectKnowledgeBaseModal;
